import type { IUser } from '@manager/api/types/auth-types';
import { buildModalRoute } from '@manager/modules/modals/routable/modal-routable-utils';
import { routeNames } from '@manager/router/route-names';
import type { RouteMetaConfig } from '@manager/types/router';
import type { RouteLocationNamedRaw } from 'vue-router';

export const routesLocation: RouteMetaConfig[] = [
  {
    path: '/locations',
    name: routeNames.locationOverview,
    component: () => import('./views/LocationListView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'locations.view',
      listFilterName: 'locations',
    },
  },
  {
    path: '/locations/:id(\\d+)',
    name: routeNames.locationDetails,
    component: () => import('./views/LocationDetailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'locations.view',
    },
    children: [
      buildModalRoute(
        {
          path: 'edit/general',
          name: routeNames.locationEditGeneral,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditGeneralForm.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/unit-settings',
          name: routeNames.locationEditUnitSettings,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditUnitSettingsForm.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/billing',
          name: routeNames.locationEditBilling,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditBillingForm.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/floors',
          name: routeNames.locationEditFloors,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditFloorsForm.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/custom-fields',
          name: routeNames.locationEditCustomFields,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditCustomFieldsForm.vue'),
        },
        { fullscreen: false, width: 'lg' }
      ),
      buildModalRoute(
        {
          path: 'edit/contracts',
          name: routeNames.locationEditContracts,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditContractsForm.vue'),
        },
        { fullscreen: true }
      ),
      buildModalRoute(
        {
          path: 'edit/email-settings',
          name: routeNames.locationEditEmailSettings,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditEmailSettingsForm.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/late-fee',
          name: routeNames.locationEditLateFee,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
          },
          component: () => import('./views/LocationEditLateFeeForm.vue'),
        },
        { fullscreen: false, width: 'xl' }
      ),
      buildModalRoute(
        {
          path: 'edit/legal-entity',
          name: routeNames.locationEditLegalEntity,
          meta: {
            requiresAuth: true,
            permission: 'locations.update',
            additionalCheck: (user: IUser) =>
              user.tenant_features.uses_kinnovis_billing,
          },
          component: () => import('./views/LocationEditLegalEntityForm.vue'),
        },
        { fullscreen: false }
      ),
    ],
  },
];

export const locationRoutesFactory = {
  locationDetails(id: number | string): RouteLocationNamedRaw {
    return {
      name: routeNames.locationDetails,
      params: {
        id,
      },
    };
  },
  locationEditContracts(locationId: number) {
    return {
      name: routeNames.locationEditContracts,
      params: {
        id: locationId,
      },
    };
  },
};
